import { createFileRoute } from "@tanstack/react-router";
import { ServicePage, serviceHead } from "@/components/site/ServicePage";

const SLUG = "varmepumper";
const TITLE = "Varmepumper, luft-til-vand & luft-til-luft installation";
const DESC =
  "Autoriseret installation af varmepumper i København og Storkøbenhavn. Vi rådgiver, dimensionerer og installerer luft-til-vand og luft-til-luft, med tilskudshjælp.";

export const Route = createFileRoute("/varmepumper")({
  head: () => serviceHead(SLUG, TITLE, DESC),
  component: () => (
    <ServicePage
      slug={SLUG}
      heroVariant="stats"
      accentColor="oklch(0.78 0.14 60)"
      eyebrow="Varmepumper"
      title="Varmepumper, grøn varme, markant lavere regning"
      intro="Skift olie, gas eller elvarme ud med en moderne varmepumpe og spar typisk 40-70% på varmeregningen. Vi rådgiver uvildigt, dimensionerer korrekt og installerer både luft-til-vand og luft-til-luft."
      stats={[
        { value: "40-70%", label: "Typisk besparelse vs. olie/gas" },
        { value: "3-5x", label: "COP, varme pr. kWh el" },
        { value: "Lang", label: "Garanti & levetid" },
        { value: "20-30k", label: "Tilskudsmulighed" },
      ]}
      longIntro={
        <>
          <h2>Varmepumpen er Danmarks billigste opvarmning</h2>
          <p>
            En moderne luft-til-vand varmepumpe leverer 3-5 kWh varme pr. kWh el, den bruger
            (COP-faktor). Det betyder at hvis du i dag fyrer med olie eller gas, ligger din typiske
            besparelse mellem 40% og 70% af din årlige varmeudgift, og samtidig sænker du
            husstandens CO2-aftryk dramatisk. Med tilskud og lave finansieringsrenter er
            tilbagebetalingstiden for de fleste villaer 6-10 år, og varmepumpen holder typisk 15-20.
          </p>
          <p>
            Vi er autoriseret installatør og laver hele forløbet, fra dimensionering og valg af
            mærke, over selve installationen og idriftsættelsen, til ansøgning af tilskud og det
            løbende serviceeftersyn. Vi sælger ikke ét bestemt mærke; vi anbefaler det der passer
            til dit hus, dit varmesystem og dit lydkrav (varmepumpen står ofte tæt på naboen).
          </p>
        </>
      }
      bullets={[
        "Uvildig rådgivning om mærke, type og dimensionering",
        "Autoriseret installation og lovpligtig idriftsættelse",
        "Hjælp til tilskudsansøgning, typisk 20.000-30.000 kr.",
        "Service- og eftersynsaftaler med faste priser",
        "Vi løser også opgaver med eksisterende anlæg fra andre installatører",
        "Erfaring med både villa, sommerhus, parcel og rækkehus",
      ]}
      features={[
        { title: "Luft-til-vand", body: "Den fulde løsning til dit centralvarmesystem og varmt vand. Erstatter olie, gas og fjernvarme." },
        { title: "Luft-til-luft", body: "Effektiv supplerende varmekilde til sommerhus, kælder eller stueetage, også som aircondition om sommeren." },
        { title: "Hybridanlæg", body: "Kombination af varmepumpe og eksisterende gas-/oliefyr, så du kan udnytte varmepumpen i mildt vejr." },
        { title: "Service & eftersyn", body: "Årligt eftersyn forlænger levetiden og holder effektiviteten oppe. Vi laver også fejlfinding på fremmede mærker." },
        { title: "Tilskud", body: "Vi hjælper med Bygningspuljen og afgiftsrefusion, og søger på dine vegne hvis du ønsker det." },
        { title: "Styring & app", body: "Moderne varmepumper styres fra app og kan tidsstyres efter elpris og solceller." },
      ]}
      process={[
        { title: "Besigtigelse", body: "Vi måler hus, isolering, eksisterende radiatorer/gulvvarme og lytter til dine ønsker (lyd, plads, æstetik)." },
        { title: "Dimensionering & tilbud", body: "Du får et skriftligt tilbud med fast pris på konkret model, ydelse og forventet besparelse." },
        { title: "Installation", body: "Typisk 2-4 arbejdsdage. Vi koordinerer både el og VVS, du har én kontaktperson." },
        { title: "Idriftsættelse", body: "Vi indregulerer, dokumenterer, registrerer hos myndigheder og lærer dig at bruge app og system." },
      ]}
      faq={[
        { q: "Hvad koster en varmepumpe?", a: "En luft-til-vand varmepumpe koster typisk 90.000-140.000 kr. installeret før tilskud, afhængigt af husstørrelse, mærke og kompleksitet. En luft-til-luft er typisk 15.000-30.000 kr. installeret." },
        { q: "Kan jeg få tilskud?", a: "Ja, Bygningspuljen giver typisk 20.000-30.000 kr. til luft-til-vand i en ældre bolig, og du kan få afgiftsrefusion på elafgiften til varmen. Vi hjælper med ansøgningen." },
        { q: "Hvor lang tid tager installationen?", a: "En standardinstallation tager typisk 2-4 arbejdsdage. Du har varme hele tiden, vi planlægger overgangen så du aldrig står uden." },
        { q: "Larmer en varmepumpe?", a: "Moderne varmepumper er meget stille, typisk 35-45 dB(A) på 1 m afstand. Vi placerer den så hverken du eller naboen generes, og kan tilbyde ekstra lyddæmpning hvis krævet." },
        { q: "Virker varmepumpen ved -10°C?", a: "Ja. Moderne varmepumper er godkendt ned til -25°C og leverer fortsat varme effektivt ved typiske danske vintertemperaturer. COP'en falder ved meget kolde dage, men anlægget kører." },
        { q: "Service og garanti?", a: "Vi giver garanti på installationen, og leverandørerne giver typisk garanti på kompressoren. Årligt eftersyn anbefales og forlænger levetiden." },
      ]}
      related={[
        { to: "/groen-energi", title: "Grøn energi", body: "Kombinér varmepumpen med solceller og batteri for laveste mulige drift." },
        { to: "/energioptimering", title: "Energioptimering", body: "Lyt til hele huset, isolering og styring giver endnu større besparelse." },
        { to: "/tilbud", title: "Indhent tilbud", body: "Få et uforpligtende tilbud på din varmepumpe-installation." },
      ]}
    />
  ),
});
